/** FELVORA — Inmobiliaria F6 P5 · Egresos por inmueble + rentabilidad. */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Receipt, Plus, TrendingUp, TrendingDown } from "lucide-react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

const mxn = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(n || 0);
const CATEGORIAS = ["predial", "agua", "luz", "mantenimiento", "administracion", "seguro", "otro"];

export default function Egresos() {
  const [egresos, setEgresos] = useState([]);
  const [inmuebles, setInmuebles] = useState([]);
  const [rent, setRent] = useState(null);
  const [openNuevo, setOpenNuevo] = useState(false);
  const [nuevo, setNuevo] = useState({
    inmueble_id: "", categoria: "predial", monto: 0,
    fecha: new Date().toISOString().slice(0, 10), proveedor: "",
    quien_absorbe: "propietario", descripcion: "",
  });

  const cargar = () => {
    api.get("/inmobiliaria/egresos").then((r) => setEgresos(r.data)).catch(() => {});
    api.get("/inmobiliaria/egresos/rentabilidad").then((r) => setRent(r.data)).catch(() => {});
  };
  useEffect(() => {
    cargar();
    api.get("/inmobiliaria/inmuebles").then((r) => setInmuebles(r.data)).catch(() => {});
  }, []);

  const guardar = async () => {
    try {
      await api.post("/inmobiliaria/egresos", { ...nuevo, monto: parseFloat(nuevo.monto) || 0 });
      toast.success("Egreso registrado");
      setOpenNuevo(false); cargar();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="egresos-page">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">
            <Receipt className="mr-2 inline h-6 w-6 text-emerald-700" />
            Egresos por inmueble
          </h1>
          <p className="text-sm text-slate-500">Predial, servicios, mantenimiento, seguro · rentabilidad por inmueble.</p>
        </div>
        <Button data-testid="btn-nuevo-egreso" onClick={() => setOpenNuevo(true)}>
          <Plus className="mr-1 h-4 w-4" /> Registrar egreso
        </Button>
      </div>

      {rent && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <Card><CardContent className="p-3">
            <p className="text-xs text-slate-500">Ingresos (rentas cobradas)</p>
            <p className="text-2xl font-bold text-emerald-700" data-testid="ingresos-total">
              <TrendingUp className="inline h-5 w-5 mr-1" />{mxn(rent.total.ingresos)}
            </p></CardContent></Card>
          <Card><CardContent className="p-3">
            <p className="text-xs text-slate-500">Egresos</p>
            <p className="text-2xl font-bold text-red-700" data-testid="egresos-total">
              <TrendingDown className="inline h-5 w-5 mr-1" />{mxn(rent.total.egresos)}
            </p></CardContent></Card>
          <Card><CardContent className="p-3">
            <p className="text-xs text-slate-500">Neto</p>
            <p className={`text-2xl font-bold ${rent.total.neto >= 0 ? "text-emerald-700" : "text-red-700"}`}
               data-testid="neto-total">{mxn(rent.total.neto)}</p></CardContent></Card>
        </div>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">Rentabilidad por inmueble</CardTitle></CardHeader>
        <CardContent className="p-0">
          {rent?.por_inmueble.length === 0 ? <p className="p-6 text-center text-sm text-slate-500">Sin actividad en el periodo.</p> :
            <div className="divide-y">
              {(rent?.por_inmueble || []).map((b) => {
                const inm = inmuebles.find((i) => i.id === b.inmueble_id);
                return (
                  <div key={b.inmueble_id} className="p-3" data-testid={`rent-${b.inmueble_id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="font-semibold">{inm?.direccion || b.inmueble_id.slice(0, 8)}</p>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge className="bg-emerald-100 text-emerald-800">Ingresos {mxn(b.ingresos)}</Badge>
                        <Badge className="bg-red-100 text-red-800">Egresos {mxn(b.egresos)}</Badge>
                        <Badge className={b.neto >= 0 ? "bg-emerald-200 text-emerald-900" : "bg-red-200 text-red-900"}>
                          Neto {mxn(b.neto)}
                        </Badge>
                      </div>
                    </div>
                    {Object.keys(b.por_categoria).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1 text-xs">
                        {Object.entries(b.por_categoria).map(([c, m]) => (
                          <Badge key={c} variant="outline">{c}: {mxn(m)}</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          }
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Egresos ({egresos.length})</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="divide-y max-h-96 overflow-y-auto">
            {egresos.map((e) => {
              const inm = inmuebles.find((i) => i.id === e.inmueble_id);
              return (
                <div key={e.id} className="p-2 text-sm flex flex-wrap items-center justify-between gap-2" data-testid={`egr-${e.id}`}>
                  <span>{e.fecha} · <strong>{e.categoria}</strong> · {inm?.direccion || e.inmueble_id.slice(0,8)}
                    {e.proveedor ? ` · ${e.proveedor}` : ""}</span>
                  <div className="flex gap-2 items-center">
                    <Badge variant="outline">{e.quien_absorbe}</Badge>
                    <span className="font-semibold">{mxn(e.monto)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Dialog open={openNuevo} onOpenChange={setOpenNuevo}>
        <DialogContent>
          <DialogHeader><DialogTitle>Registrar egreso</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className="text-sm">Inmueble</label>
              <Select value={nuevo.inmueble_id} onValueChange={(v) => setNuevo({...nuevo, inmueble_id: v})}>
                <SelectTrigger data-testid="egr-inmueble"><SelectValue placeholder="Elige..." /></SelectTrigger>
                <SelectContent>{inmuebles.map(i => <SelectItem key={i.id} value={i.id}>{i.direccion}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><label className="text-sm">Categoría</label>
              <Select value={nuevo.categoria} onValueChange={(v) => setNuevo({...nuevo, categoria: v})}>
                <SelectTrigger data-testid="egr-categoria"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIAS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><label className="text-sm">Monto</label>
              <Input type="number" data-testid="egr-monto" value={nuevo.monto} onChange={(e) => setNuevo({...nuevo, monto: e.target.value})} /></div>
            <div><label className="text-sm">Fecha</label>
              <Input type="date" data-testid="egr-fecha" value={nuevo.fecha} onChange={(e) => setNuevo({...nuevo, fecha: e.target.value})} /></div>
            <div><label className="text-sm">Proveedor</label>
              <Input value={nuevo.proveedor} onChange={(e) => setNuevo({...nuevo, proveedor: e.target.value})} /></div>
            <div><label className="text-sm">Absorbe</label>
              <Select value={nuevo.quien_absorbe} onValueChange={(v) => setNuevo({...nuevo, quien_absorbe: v})}>
                <SelectTrigger data-testid="egr-absorbe"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="propietario">Propietario</SelectItem>
                  <SelectItem value="inmobiliaria">La entidad</SelectItem>
                </SelectContent>
              </Select></div>
            <div className="col-span-2"><label className="text-sm">Descripción (opcional)</label>
              <Input value={nuevo.descripcion} onChange={(e) => setNuevo({...nuevo, descripcion: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenNuevo(false)}>Cancelar</Button>
            <Button data-testid="btn-guardar-egreso" onClick={guardar}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
